import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

// Hooks
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

// Actions
import {
  fetchOrderContactDetails,
  removeContact,
  fetchOrderOverviewDetails,
  searchAndAdvanceChange,
} from '~/redux/actions/orders-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Utils
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION } from '~/utils/constants';
import { MESSAGES } from '~/utils/message';
import { doesItFallInCancellationPeriod, dateFormat } from '~/utils/date-format';
import { stringLength } from '~/utils/helperFunctions';

// Components
import ConfirmDialog from '~/components/Orders/ConfirmDialog/ConfirmDialog';
import OlcBulkSelectionDropdown from '~/components/General/OlcBulkSelectionDropdown';

// Mui Components
import DataGrid from '~/components/General/OlcDataGrid';
import {
  Box,
  Pagination,
  PaginationItem,
  Select,
  MenuItem,
  Typography,
  Tooltip,
  Stack,
  Menu,
  Checkbox,
  IconButton,
  Chip,
} from '@mui/material';

// Icons
import {
  CancelOrderDisabledCIcon,
  RemoveCIcon,
  CancelOrderCIcon,
  NoContactCIcon,
} from '~/assets/images';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DoneIcon from '@mui/icons-material/Done';
import RemoveIcon from '@mui/icons-material/Remove';

// styles
import './styles.scss';

/**
 * TemplateListing Component
 * Displays a list of templates with various actions.
 * @returns {JSX.Element} The data grid component showing list templates.
 */
const ContactListing = ({
  setSelectedRows,
  selectedRows,
  searchApplied,
  totalContactsInDB,
  filterSize,
  filters,
}) => {
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [showNoRows, setShowNoRows] = useState(false);

  // bullk actions
  const [bulkSelect, setBulkSelect] = useState(false);
  const [removeBulkSelect, setRemoveBulkSelect] = useState(false);
  const [rows, setRows] = useState([]);
  const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false);
  const hasMountedPaginationEffect = useRef(false);

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const search = useSelector((state) => state.ordersReducers.detailContacts.search);
  const contacts = useSelector((state) => state.ordersReducers.detailContacts);

  const detail = useSelector((state) => state.ordersReducers.detailOverview);

  const fetchedRows = useSelector((state) => state.ordersReducers.detailContacts.list) || [];
  const to = useSelector((state) => state.ordersReducers.detailContacts.to);
  const from = useSelector((state) => state.ordersReducers.detailContacts.from);
  const lastPage = useSelector((state) => state.ordersReducers.detailContacts.lastPage || 1);
  const perPage =
    useSelector((state) => state.ordersReducers.detailContacts.perPage) ||
    DEFAULT_PAGINATION.pageSize;

  const currentPage = useSelector((state) => state.ordersReducers.detailContacts.currentPage || 1);
  const rowsCount = useSelector((state) => state.ordersReducers.detailContacts.rowsCount);

  const [dialog, setDialog] = useState({
    open: false,
    message: '',
    loading: false,
    currentAction: null,
    params: null,
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pageSizes = DEFAULT_PAGE_SIZES;

  const { id } = useParams();

  const getAllContacts = () => {
    const hasFilters = filterSize > 0;
    dispatch(
      fetchOrderContactDetails({
        id,
        pagination,
        search,
        filters: hasFilters ? filters : null,
        applyFilters: hasFilters,
      })
    );
  };

  const handleCloseDialog = () => {
    setDialog({
      open: false,
      message: '',
      loading: false,
      params: null,
      currentAction: null,
    });
  };

  const handleOk = async () => {
    setDialog((prev) => ({ ...prev, loading: true }));
    if (dialog.currentAction === 'remove') {
      const payload = {
        orderId: dialog.params.row.orderId,
        contactId: dialog.params.row.contactId,
      };
      const response = await dispatch(removeContact(payload));
      if (response.status === 200) {
        dispatch(success(response.data.message));
        getAllContacts();
        const result = await dispatch(fetchOrderOverviewDetails({ id }));

        if (result && result.status === 422 && result.shouldRedirect) {
          navigate('/orders');
        }
      } else {
        dispatch(failure(response.data.message));
      }
    }
    setDialog((prev) => ({ ...prev, loading: false, open: false }));
  };

  const handleCancelOrder = async (params) => {
    setDialog({
      open: true,
      message: 'Are you sure you want to remove this contact?',
      loading: false,
      currentAction: 'remove',
      params: params,
      successButtonName: 'Yes',
      subMessage: 'This action cannot be undone. Please proceed with caution.',
      heading: 'Remove Contact?',
      icon: CancelOrderCIcon,
    });
  };

  const handleChangePage = (value, type) => {
    let nextPagination;

    if (type == 'pageSize') {
      nextPagination = {
        page: DEFAULT_PAGINATION.page,
        pageSize: value,
      };
    } else {
      nextPagination = {
        page: value,
        pageSize: pagination.pageSize,
      };
    }

    setPagination(nextPagination);
    dispatch(searchAndAdvanceChange('contactsPagination', nextPagination));
  };

  useEffect(() => {
    Promise.resolve(getAllContacts()).finally(() => {
      setHasCompletedInitialLoad(true);
    });

    // dispatch(fetchOrderOverviewDetails({ id }));

    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!hasMountedPaginationEffect.current) {
      hasMountedPaginationEffect.current = true;
      return;
    }

    getAllContacts();
  }, [pagination]);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const selectAll = () => {
    setBulkSelect(true);
    const updatedRows = rows.map((contact) => ({ ...contact, checked: true }));
    setRows(updatedRows);
    setSelectedRows([-1]);
  };

  const selectVisble = () => {
    setBulkSelect(true);
    const updatedRows = rows.map((contact) => ({ ...contact, checked: true }));
    setRows(updatedRows);

    const selectedRowIds = updatedRows.map((contact) => contact.contactId);
    setSelectedRows(selectedRowIds);
  };

  const selectNone = () => {
    setBulkSelect(false);
    setRemoveBulkSelect(false);
    const updatedRows = rows.map((contact) => ({ ...contact, checked: false }));
    setRows(updatedRows);
    setSelectedRows([]);
  };

  const selectRow = (event, selectedRow) => {
    event.stopPropagation();

    // Update the selectedRow object directly in the rows array
    const updatedRows = rows.map((row) =>
      row.id === selectedRow.id ? { ...row, checked: !row.checked } : row
    );

    // If the user selects all contacts and then removes a single contact, update the selected rows array accordingly.
    if (selectedRows[0] === -1) {
      const filteredRows = rows.map((row) =>
        row.id === selectedRow.id ? { ...row, checked: !row.checked } : { ...row, checked: true }
      );

      const filteredRowsIds = filteredRows
        .map((contact) => contact.checked === true && contact.contactId)
        .filter(Boolean);

      setSelectedRows(filteredRowsIds);
      setRows(updatedRows);
      setRemoveBulkSelect(true);
      return;
    }

    setRows(updatedRows);

    const selectedRowIds = selectedRows.includes(selectedRow.contactId)
      ? selectedRows.filter((id) => id !== selectedRow.contactId) // Remove the ID if already selected
      : [...selectedRows, selectedRow.contactId]; // Add the ID if not selected

    // Update the selectedRows state with the new array
    setSelectedRows(selectedRowIds);

    setRemoveBulkSelect(true);
  };

  const bulkSelection = () => {
    const newBulkSelect = !bulkSelect;
    setBulkSelect(newBulkSelect);

    const updatedRows = rows.map((contact) => ({
      ...contact,
      checked: newBulkSelect,
    }));
    setRows(updatedRows);

    const selectedRowIds = updatedRows.map((contact) => contact.contactId);
    newBulkSelect ? setSelectedRows(selectedRowIds) : setSelectedRows([]);
  };

  const removeBulkSelection = () => {
    const newBulkSelect = !removeBulkSelect;
    setRemoveBulkSelect(newBulkSelect);
    setBulkSelect(newBulkSelect);

    const updatedRows = rows.map((contact) => ({
      ...contact,
      checked: newBulkSelect,
    }));
    setRows(updatedRows);
    setSelectedRows([]);
  };

  const formatFullAddress = (recipient = {}) =>
    [recipient.address1, recipient.address2, recipient.city, recipient.state, recipient.zip]
      .filter(Boolean)
      .join(', ');

  const columns = [
    {
      field: ' ',
      headerClassName: 'olcBulkSelectHeader',
      cellClassName: 'olcBulkSelectCell',
      renderHeader: () => (
        <Box className="checkboxHeader orderDetailsContacts">
          {removeBulkSelect ? (
            <Checkbox
              className="bulkCheck"
              icon={<Box className="bulkNotchecked" />}
              checkedIcon={<RemoveIcon />}
              checked={removeBulkSelect}
              onClick={removeBulkSelection}
            />
          ) : (
            <Checkbox
              className="bulkCheck"
              icon={<Box className="bulkNotchecked" />}
              checkedIcon={<DoneIcon />}
              checked={bulkSelect}
              onClick={bulkSelection}
            />
          )}
          <OlcBulkSelectionDropdown
            ariaLabel="Open order contact selection options"
            onClick={handleClick}
            open={open}
          />
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            classes={{
              paper: 'menuOptionsPaper',
              list: 'menuOptionsList',
            }}
            sx={{
              marginTop: '7px',
            }}
          >
            <MenuItem onClick={selectAll} className={selectedRows[0] === -1 ? 'highlight' : ''}>
              {MESSAGES.ORDERS.DETAIL.CONTACTS.TABLE.SELECT_ALL_ITEMS} ({rowsCount})
            </MenuItem>
            <MenuItem onClick={selectNone}>
              {MESSAGES.ORDERS.DETAIL.CONTACTS.TABLE.SELECT_NONE}
            </MenuItem>
            <MenuItem onClick={selectVisble}>
              {MESSAGES.ORDERS.DETAIL.CONTACTS.TABLE.SELECT_VISIBLE_ITEMS}
            </MenuItem>
          </Menu>
        </Box>
      ),
      width: 75,
      sortable: false,
      renderCell: (params) => (
        <Box className="checkboxRow orderDetailsContactsRow">
          <Checkbox
            onClick={(event) => selectRow(event, params.row)}
            className="bulkCheck"
            icon={<Box className="bulkNotchecked" />}
            checkedIcon={<DoneIcon />}
            checked={params.row.checked}
          />
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Full Name',
      headerClassName: 'olcBulkSelectAdjacentHeader',
      width: 210,
      sortable: false,
      renderCell: (params) => {
        const recipient = params?.row?.recipient;
        const displayName =
          recipient?.fullName ||
          `${recipient?.firstName || ''} ${recipient?.lastName || ''}`.trim() ||
          '-';

        const renderNameContent = () => {
          const nameElement = <Typography>{displayName}</Typography>;

          return params?.row?.contactId ? (
            <Box className="valWrapper nameChipWrapper">
              <Typography>{displayName}</Typography>
            </Box>
          ) : (
            // <Box className="fullNameWrapper">
            //   {nameElement}
            //   {(params?.row?.dncMarkedAt || recipient?.dncMarkedAt) && (
            //     <Tooltip title="Do Not Contact">
            //       <Chip label="DNC" color="primary" className="returnAddressChip" />
            //     </Tooltip>
            //   )}
            // </Box>
            <Box className="valWrapper nameChipWrapper">
              <Typography>{displayName}</Typography>
              {(params?.row?.dncMarkedAt || recipient?.dncMarkedAt) && (
                <Tooltip title="Do Not Contact">
                  <Chip label="DNC" color="primary" className="returnAddressChip" />
                </Tooltip>
              )}
            </Box>
          );
        };

        return stringLength(displayName, 20) ? (
          <Tooltip title={displayName}>{renderNameContent()}</Tooltip>
        ) : (
          renderNameContent()
        );
      },
    },
    {
      field: 'company_name',
      headerName: 'Company Name',
      type: 'number',
      width: 210,
      sortable: false,
      flex: window.innerWidth > 1600 ? 1 : 0,
      renderCell: (params) =>
        stringLength(`${params?.row?.recipient?.companyName}`, 25) ? (
          <Tooltip title={params?.row?.recipient?.companyName}>
            <Box className="valWrapper">
              <Typography>{params?.row?.recipient?.companyName || '-'}</Typography>
            </Box>
          </Tooltip>
        ) : (
          <Box className="valWrapper">
            <Typography>{params?.row?.recipient?.companyName || '-'}</Typography>
          </Box>
        ),
    },
    {
      field: 'address',
      headerName: 'Full Address',
      width: 205,
      sortable: false,
      flex: window.innerWidth > 1600 ? 1 : 0,
      renderCell: (params) => {
        const fullAddress = formatFullAddress(params?.row?.recipient);

        return stringLength(fullAddress, 30) ? (
          <Tooltip title={fullAddress}>
            <Box className="addressWrapper">
              <Typography>{fullAddress || '-'}</Typography>
            </Box>
          </Tooltip>
        ) : (
          <Box className="addressWrapper">
            <Typography>{fullAddress || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Address Status',
      width: 205,
      sortable: false,
      flex: window.innerWidth > 1600 ? 1 : 0,
      renderCell: (params) => (
        <Box className="valWrapper">
          <Typography>{params?.row?.recipient?.addressStatus || '-'}</Typography>
        </Box>
      ),
    },
    {
      field: 'mailed_status',
      headerName: 'Mailed Status',
      width: 205,
      sortable: false,
      flex: window.innerWidth > 1600 ? 1 : 0,
      renderCell: (params) => (
        <Box className="valWrapper">
          <Typography>{params?.row?.status || '-'}</Typography>
        </Box>
      ),
    },
    {
      field: 'deliveredAt',
      headerName: 'Delivered Date',
      width: 205,
      sortable: false,
      flex: window.innerWidth > 1600 ? 1 : 0,
      renderCell: (params) => (
        <Tooltip title={params?.row?.deliveredDate ? dateFormat(params?.row?.deliveredDate) : ''}>
          <Box className="valWrapper">
            <Typography>
              {params?.row?.deliveredDate ? dateFormat(params?.row?.deliveredDate) : '-'}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
  ];

  useEffect(() => {
    setSelectedRows([]);
  }, [currentPage, perPage]);

  useEffect(() => {
    !selectedRows.length && setRemoveBulkSelect(false);
    (rows.length && selectedRows.length === rows.length) || selectedRows[0] === -1
      ? (setBulkSelect(true), setRemoveBulkSelect(false))
      : setBulkSelect(false);
  }, [selectedRows, rows]);

  useLayoutEffect(() => {
    setRows(fetchedRows);
  }, [fetchedRows]);

  useEffect(() => {
    if (rows.length === 0 && currentPage > 1) {
      handleChangePage(currentPage - 1, 'page');
    }
  }, [rows]);

  const showTableLoading = contacts.loading || (!hasCompletedInitialLoad && rows.length === 0);
  const shouldReserveLoaderHeight = showTableLoading;
  const showNoResultsState = !showTableLoading && rows.length === 0;
  const displayRows = showTableLoading ? [] : rows;

  // Add delay for showing no rows overlay
  useEffect(() => {
    let timeoutId;

    if (showNoResultsState) {
      // Show "No Results Found" after a delay when not loading and no data
      timeoutId = setTimeout(() => {
        setShowNoRows(true);
      }, 300); // 300ms delay
    } else {
      // Hide immediately when loading or when we have data
      setShowNoRows(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [showNoResultsState]);

  return (
    <div
      style={{
        marginTop: '20px',
        width: '100%',
      }}
      className={`orderDetailsInfoTable ${shouldReserveLoaderHeight ? 'initialLoadingTable' : ''} ${showNoResultsState ? 'noResultsTable' : ''}`}
    >
      <DataGrid
        rows={displayRows}
        columns={columns}
        rowCount={rowsCount}
        paginationMode="server"
        hideFooterSelectedRowCount
        hideFooterPagination
        rowSelection={false}
        getRowId={(row) => row?.id}
        rowHeight={61}
        autoHeight
        fillAvailableWidth
        shrinkColumnsToFit
        getRowClassName={(params) => {
          return selectedRows.includes(params.row.contactId) ? 'selectedRow' : '';
        }}
        disableColumnMenu={true}
        classes={{
          withBorderColor: 'borderClass',
          columnHeaders: 'orderDetailsContactsHeaders',
          columnHeadersInner: 'orderDetailsContactsHeadersInner',
          columnHeader: 'orderDetailsContactsHeader',
          row: 'orderDetailsContactsRow',
          cell: 'orderDetailsContactsCell',
          virtualScroller: `tableGridVirtualScroller ${!showTableLoading && rows.length >= 10 ? 'heightAdjust' : ''} ${shouldReserveLoaderHeight ? 'initialLoading' : ''} ${showNoResultsState ? 'noResults' : ''}`,
        }}
        className="orderDetailsTable"
        loading={showTableLoading}
        slots={{
          noRowsOverlay: () =>
            showNoRows ? <Typography className="noResultsText">No Results Found</Typography> : null,
          noResultsOverlay: () =>
            showNoRows ? <Typography className="noResultsText">No Results Found</Typography> : null,
        }}
      />
      {(searchApplied || filterSize > 0 ? rowsCount >= 10 : totalContactsInDB > 10) && (
        <Box className="productTablePagination">
          <Pagination
            count={lastPage}
            variant="outlined"
            shape="rounded"
            onChange={(e, newVal) => handleChangePage(newVal, 'page')}
            page={currentPage}
            renderItem={(item) => (
              <PaginationItem
                slots={{
                  previous: ArrowBackIosNewIcon,
                  next: ArrowForwardIosIcon,
                }}
                {...item}
              />
            )}
          />
          <Select
            className={pagination.pageSize >= 100 ? `pageSelect pageSelectChange` : `pageSelect`}
            inputProps={{ 'aria-label': 'Results per page' }}
            value={pagination.pageSize}
            MenuProps={{
              classes: {
                paper: 'pageSelectPaper',
              },
            }}
          >
            {pageSizes.map((pageSize) => {
              return (
                <MenuItem
                  key={pageSize}
                  value={pageSize}
                  onClick={() => {
                    handleChangePage(pageSize, 'pageSize');
                  }}
                >
                  {pageSize}
                </MenuItem>
              );
            })}
          </Select>
          <Typography>
            Showing {rowsCount ? from : 0} to {rowsCount < to ? rows?.length + from - 1 : to} of{' '}
            {rowsCount} results
          </Typography>
        </Box>
      )}
      {dialog.open && (
        <ConfirmDialog
          dialog={dialog}
          open={dialog.open}
          loading={dialog.loading}
          handleClose={handleCloseDialog}
          handleOk={handleOk}
        />
      )}
    </div>
  );
};

export default ContactListing;
